import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Page, Group, Layout, Button } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useResource, useModal, useToast, useAuth, usePage } from "@/comn/hooks"; // hook
import { APIS, SF_USR_RGSR_APLY_INFO } from "@/tra/tancis/ptli/acrdt/newusr/service/PtliUsrRgsrAplyService";
import { comnUtils } from "@/comn/utils";

/*
 * User Registration Application Information
 * !== 사용자 등록 신청 정보 ==!
 */
export const PTLI0205001S = (): JSX.Element => {
    const { menuId } = usePage();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal();
    const toast = useToast();
    const auth = useAuth();
    const { usrDmnId } = useParams();
    const [searchParams] = useSearchParams();

    const [btns, setBtns] = useState({
        search: true,
        regist: true,
    });

    const [requiredYn, setRequiredYn] = useState("Y");

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "PTL0003" },
            { area: "cstmCd" },
            { area: "orgz" },
            { area: "orgzSub" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의. ==!
     */
    const form = {
        // Apply for User Registration Form !== 사용자 등록 신청 폼 ==!
        usrAplyInfo: useForm({ defaultSchema: SF_USR_RGSR_APLY_INFO }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Search user registration application information based on login ID !== 로그인ID 기준 사용자 등록 신청 정보 조회==!
        getUsrInfo: useFetch({
            api: (data: any) => APIS.getUsr(data),
            enabled: false,
            onSuccess: (data) => {
                if (data.usrAplyInfo.content !== undefined) {
                    const initMyInfoData = data.usrAplyInfo.content;

                    form.usrAplyInfo.setValues({ ...initMyInfoData, fileGrpUuid: `R_${initMyInfoData.usrDmnId}` });

                    if (initMyInfoData.aprvYn === "N") {
                        setBtns((btns: any) => ({ ...btns, regist: false }));
                    }

                    handler.getCstmList("Y");
                    handler.getUnitList("Y");
                    handler.getCstmRoleList("Y");
                }
            },
            onError: () => { },
            showToast: true,
        }),
        // Inquire user application information!== 사용자 신청 정보 조회 ==!
        getUsrRgsrAplyInfo: useFetch({
            api: (data) => APIS.getUsrRgsrAplyInfo(data),
            enabled: false,
            onSuccess: (data) => {
                form.usrAplyInfo.setValue("aprvPrcssStatCdNm", "");

                if (data.usrAplyInfo.content) {
                    form.usrAplyInfo.setError("cstmOfcrNo", {
                        message: "msg.com.00107", // Already registered user. !== 이미 등록된 사용자입니다. ==!
                        type: "error",
                    });

                    form.usrAplyInfo.setValue("aprvPrcssStatCdNm", data.usrAplyInfo.content.aprvPrcssStatCdNm);
                    return;
                }

                const content = fetch.getHrUsrInfo.getData().usrHrInfo.content;
                form.usrAplyInfo.setValue("usrDmnId", content.usrDmnId);
                form.usrAplyInfo.setValue("usrNm", content.usrNm);
                form.usrAplyInfo.setValue("usrTelno", content.usrTelno);
                form.usrAplyInfo.setValue("usrEmlAddr", content.usrEmlAddr);
                form.usrAplyInfo.setValue("usrTin", content.usrTin);
                handler.getUsrTinInfo();

                form.usrAplyInfo.clearErrors("cstmOfcrNo");
            },
            onError: () => { },
            showToast: true,
        }),

        // HR system employee number inquiry !== HR시스템 직원번호 조회 ==!
        getHrUsrInfo: useFetch({
            api: (cstmOfcrNo) => APIS.getHrUsrInfo(cstmOfcrNo),
            enabled: false,
            onSuccess: (data) => {
                if (data.usrHrInfo.content) {
                    const { usrDmnId, cstmOfcrNo } = data.usrHrInfo.content;
                    fetch.getUsrRgsrAplyInfo.fetch({ usrDmnId, cstmOfcrNo });
                } else {
                    form.usrAplyInfo.setError("cstmOfcrNo", {
                        message: "msg.com.00108", // There is no customs employee number registered in the HR system. !== HR시스템에 등록된 세관직원번호가 없습니다.  ==!
                        type: "error",
                    });
                    const actnUsrYn = form.usrAplyInfo.getValue("actnUsrYn");
                    form.usrAplyInfo.clearValues();
                    form.usrAplyInfo.setValue("actnUsrYn", actnUsrYn);
                }
            },
            onError: () => { },
            showToast: true,
        }),

        // Check TIN validity !== TIN 유효성을 체크 후 사용자 정보를 저장한다. ==!
        getUsrTinInfo: useFetch({
            api: () => APIS.getTinNoLst(form.usrAplyInfo.getValue("usrTin"), "N"),
            onSuccess: (data) => {
                const tinNoList = data.tinNoList.content;

                if (tinNoList.length == 1) {
                    form.usrAplyInfo.clearErrors("usrTin");
                } else {
                    // The TIN does not exist. Please recheck the TIN.
                    form.usrAplyInfo.setValue("usrTin", "");
                    toast.showToast({ type: "error", content: "msg.clr.00004" });
                }
            },
        }),

        // Section search by customs !== 섹션별 세관 조회 ==!
        getCstmList: useFetch({
            api: () => APIS.getOrgzList(form.usrAplyInfo.getValues()),
            enabled: false,
            onSuccess: (data) => {
                if (data.orgzList.content.length > 0) {
                    form.usrAplyInfo.setValue(
                        "cstmOfceCdArr",
                        data.orgzList.content.map((item: any) => item.cstmOrgzCd),
                    );
                }
                handler.getCstmRoleList(form.usrAplyInfo.getValue("loadYn"));
                handler.getAprvUsrList();
            },
            onError: () => { },
            showToast: true,
        }),

        // unit search by section !== 섹션별 유닛 조회 ==!
        getUnitList: useFetch({
            api: () => APIS.getOrgzList(form.usrAplyInfo.getValues()),
            enabled: false,
            onSuccess: (data) => {
                if (data.orgzList.content.length > 0) {
                    form.usrAplyInfo.setSchema("cstmUnitOrgzCd", {
                        options: data.orgzList.content.map((item: any, index: number) => ({
                            label: item.cstmOrgzNm,
                            value: item.cstmOrgzCd,
                        })),
                    });
                }
                handler.getCstmRoleList(form.usrAplyInfo.getValue("loadYn"));
            },
            onError: () => { },
            showToast: true,
        }),

        // Section search by customs role !== 조직 역할 목록 조회 ==!
        getCstmRoleList: useFetch({
            api: (objParam) => APIS.getCstmRoleList(objParam),
            onSuccess: (data) => {
                if (data.cstmRoleList.content.length > 0) {
                    form.usrAplyInfo.setSchema("roleId", {
                        options: data.cstmRoleList.content.map((item: any) => ({
                            label: item.roleNm,
                            value: item.roleId,
                        })),
                    });
                } else {
                    form.usrAplyInfo.setSchema("roleId", { options: [] });
                    form.usrAplyInfo.setValue("roleId", "");
                }
            },
            onError: () => { },
            showToast: true,
        }),

        // Save User Registration Applications !== 사용자 등록 신청 저장 ==!
        saveUsrRgsrAply: useFetch({
            api: (data) => APIS.saveUsrRgsrAply({ ...data }),
            onSuccess: (data) => {
                form.usrAplyInfo.setValue("histSrno", data.usrRgsrAplyInfo.content.histSrno);

                if (form.usrAplyInfo.getValue("saveType") === "S") {
                    modal.openModal({
                        content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                        onConfirm: () => navigate("/login"),
                        close: false,
                    });
                }
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.usrAplyInfo.setErrors(error.response.data.errors);
                }

                // An error occurred during processing.
                modal.openModal({ content: "msg.00006" });
            },
        }),

        // Enter the payment ID in the user application information. !== 사용자 신청 정보에 결재아이디를 넣어준다. ==!
        saveUsrAplyId: useFetch({
            api: (data) => APIS.saveUsrAplyId({ ...data }),
            onSuccess: () => {
                form.usrAplyInfo.setValue("aprvPrcssStatCdNm", "Approval Request");
                modal.openModal({
                    content: "msg.ptl.00814",
                });
            },
            onError: (error) => {
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.usrAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({ content: "msg.00006" }); // An error occurred during processing.
            },
        }),

        // User Tin duplicate check !== 사용자 TIN 중복 체크 ==!
        getBidrDuplCheck: useFetch({
            api: (data: any) => APIS.getBidrDuplCheck(data),
            enabled: false,
            onSuccess: (data) => {
                form.usrAplyInfo.clearErrors("usrTin");

                const extUsrInfoContent = data.bidrUsrInfo.content;
                if (!comnUtils.isEmpty(extUsrInfoContent)) {
                    form.usrAplyInfo.setValue("xtrnUsrRgsrYn", "Y");
                } else {
                    form.usrAplyInfo.setValue("xtrnUsrRgsrYn", "N");
                }
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        getAprvUsrList: useFetch({
            api: () => APIS.getAprvUsrList(form.usrAplyInfo.getValues()),
            enabled: false,
            onSuccess: (data) => {
                if (data.aprvUsrList.content.length > 0) {
                    // 여기서 array 집어 넣어야함
                    form.usrAplyInfo.setValue(
                        "aprvUsrList",
                        data.aprvUsrList.content.map((item: any) => item.usrDmnId),
                    );
                }
            },
            onError: () => { },
        }),

        // Get Section manager Y/N !== 섹션매니저 여부 조회 ==!
        getSctnMngrYn: useFetch({
            api: (data) => APIS.getSctnMngrYn(data),
            enabled: false,
            onSuccess: (data) => {
                // Approver setting !== 결재자 세팅 ==!
                const sctnMngrYn = data.sctnMngrYn.content;
                const sctnCd = form.usrAplyInfo.getValue("cstmSctnOrgzCd");
                if (sctnCd == "CBS") {
                    // Section : CBS
                    form.usrAplyInfo.setValue("authAprvRqstList", [
                        { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                    ]);
                } else if (sctnCd == "DC") {
                    // Section : DC(DEPUTY COMMISSIONERS)
                    form.usrAplyInfo.setValue("authAprvRqstList", [
                        { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0039" }, // CCE
                        { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                    ]);
                } else if (sctnMngrYn == "Y") {
                    // Role : Section Manager
                    form.usrAplyInfo.setValue("authAprvRqstList", [
                        { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0035" }, // DCCO
                        { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                        { aprvPrcssOrd: "3", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                    ]);
                } else {
                    // Role : Officer
                    form.usrAplyInfo.setValue("authAprvRqstList", [
                        { aprvPrcssOrd: "1", usrDmnId: "auth_ptli0030", cstmSctnOrgzCd: sctnCd }, // SECTION MANAGER
                        { aprvPrcssOrd: "2", usrDmnId: "auth_ptli0038" }, // CAS OFFICER
                        { aprvPrcssOrd: "3", usrDmnId: "auth_ptli0037" }, // CAS MANAGER
                    ]);
                }

                handler.onAprvRqstBtnClick();
            },
            onError: () => { },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Double check for user application !== 사용자 신청 중복 체크 ==!
        // 사용자의 전자결제 상태에 따라서 메시지가 변경되어야 함
        // 결재전/ 결재중 -> 신청중
        // 결재완료 -> 이미 등록된 직원
        getUsrRgsrAplyInfo: () => {
            const cstmOfcrNo = form.usrAplyInfo.getValue("cstmOfcrNo");
            if (comnUtils.isEmpty(cstmOfcrNo)) {
                form.usrAplyInfo.setError("cstmOfcrNo", {
                    message: "msg.com.00005", // Mandatory fields !== 필수 입력 필드 ==!
                    type: "error",
                });
                return;
            }

            form.usrAplyInfo.clearErrors("cstmOfcrNo");
            cstmOfcrNo && fetch.getHrUsrInfo.fetch(cstmOfcrNo);
        },

        // Check TIN validity !== TIN 유효성을 체크한다. ==!
        getUsrTinInfo: () => {
            const usrTin = form.usrAplyInfo.getValue("usrTin");
            if (usrTin) fetch.getUsrTinInfo.fetch(usrTin);
        },

        changeCstmOfcrNo: () => {
            if (form.usrAplyInfo.getValue("usrDmnId")) {
                form.usrAplyInfo.setValue("usrDmnId", "");
                form.usrAplyInfo.setValue("usrNm", "");
                form.usrAplyInfo.setValue("usrTelno", "");
                form.usrAplyInfo.setValue("usrEmlAddr", "");
                form.usrAplyInfo.setValue("usrTin", "");
            }
        },
        changeUsrTin: () => {
            // Please check your TIN. !== TIN을 조회해주세요. ==!
            form.usrAplyInfo.setError("usrTin", { message: "msg.clr.00004", type: "error" });
        },

        changeActnUsrYn: () => {
            setRequiredYn(form.usrAplyInfo.getValue("actnUsrYn"));
        },

        checkPwd: () => {
            const usrPwd = form.usrAplyInfo.getValue("usrPwd");
            const usrPwdCfrm = form.usrAplyInfo.getValue("usrPwdCfrm");

            if (!comnUtils.isEmpty(usrPwd) && !comnUtils.equals(usrPwd, usrPwdCfrm)) {
                // Passwords do not match. !== 비밀번호가 일치하지 않습니다. ==!
                form.usrAplyInfo.setError("usrPwdCfrm", { message: "msg.ptl.00712", type: "error" });
            } else {
                form.usrAplyInfo.clearErrors("usrPwdCfrm");
            }
        },

        // Section search by customs !== 섹션별 세관 조회 ==!
        getCstmList: (loadYn: any) => {
            if (loadYn !== "Y") {
                form.usrAplyInfo.setSchema("cstmOfceCd", { options: [] });
                form.usrAplyInfo.setSchema("cstmUnitOrgzCd", { options: [] });
                form.usrAplyInfo.setSchema("roleId", { options: [] });

                form.usrAplyInfo.setValue("cstmOfceCd", "");
                form.usrAplyInfo.setValue("cstmUnitOrgzCd", "");
                form.usrAplyInfo.setValue("roleId", "");
            }

            form.usrAplyInfo.setValue("cstmOrgzTpCd", "C");
            form.usrAplyInfo.setValue("loadYn", "Y");

            fetch.getCstmList.fetch();
        },

        // unit search by section !== 세관별 유닛 조회 ==!
        getUnitList: (loadYn: any) => {
            if (loadYn !== "Y") {
                form.usrAplyInfo.setSchema("cstmUnitOrgzCd", { options: [] });
                form.usrAplyInfo.setSchema("roleId", { options: [] });

                form.usrAplyInfo.setValue("cstmUnitOrgzCd", "");
                form.usrAplyInfo.setValue("roleId", "");
            }

            form.usrAplyInfo.setValue("cstmOrgzTpCd", "U");
            form.usrAplyInfo.setValue("loadYn", "Y");

            fetch.getUnitList.fetch();
        },

        // Section search by customs role !== 세관 역할 조회 ==!
        getCstmRoleList: (loadYn: any) => {
            if (loadYn !== "Y") {
                form.usrAplyInfo.setSchema("roleId", { options: [] });
                form.usrAplyInfo.setValue("roleId", "");
            }

            const cstmSctnOrgzCd = form.usrAplyInfo.getValue("cstmSctnOrgzCd");

            // Initialize role combo if there is no section code !== 섹션 코드 없는 경우 역할 콤보 초기화 ==!
            if (comnUtils.isEmpty(cstmSctnOrgzCd)) {
                form.usrAplyInfo.setSchema("roleId", { options: [] });
                form.usrAplyInfo.setValue("roleId", "");
                return;
            }

            const cstmOfceCd =
                form.usrAplyInfo.getValue("cstmOfceCd") === null ? "" : form.usrAplyInfo.getValue("cstmOfceCd");
            const cstmUnitOrgzCd =
                form.usrAplyInfo.getValue("cstmUnitOrgzCd") === null ? "" : form.usrAplyInfo.getValue("cstmUnitOrgzCd");
            const cstmOrgzTpCd = comnUtils.isEmpty(cstmOfceCd) ? "S" : !comnUtils.isEmpty(cstmUnitOrgzCd) ? "U" : "C";
            const objParam = { cstmSctnOrgzCd, cstmOfceCd, cstmUnitOrgzCd, cstmOrgzTpCd };
            fetch.getCstmRoleList.fetch(objParam);
        },

        getAprvUsrList: () => {
            fetch.getAprvUsrList.fetch();
        },

        // Save User Registration Applications !== 사용자 등록 신청 저장 ==!
        saveUsrRgsrAply: (saveType: any) => {
            if (!comnUtils.isEmpty(form.usrAplyInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.usrAplyInfo.setValue("saveType", saveType);

            form.usrAplyInfo.handleSubmit(
                () => {
                    // 결재 없이 SAVE
                    if (saveType === "S") {
                        modal.openModal({
                            content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                            onConfirm: () => {
                                const data = form.usrAplyInfo.getValues();

                                if (typeof data.fileGrpUuid === "object") {
                                    data.fileSaveDto = data.fileGrpUuid;
                                    data.fileGrpUuid = null;
                                }
                                fetch.saveUsrRgsrAply.fetch(data);
                            },
                        });
                        return;
                    }

                    fetch.getSctnMngrYn.fetch(form.usrAplyInfo.getValue("roleId"));
                    //handler.onAprvRqstBtnClick();
                },
                () => toast.showToast({ type: "warning", content: "msg.00002" }),
            )();
        },

        // Enter the payment ID in the user application information. !== 사용자 신청 정보에 결재아이디를 넣어준다. ==!
        saveUsrAplyId: () => {
            if (!comnUtils.isEmpty(form.usrAplyInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            form.usrAplyInfo.handleSubmit(
                () => fetch.saveUsrAplyId.fetch(form.usrAplyInfo.getValues()),
                () => toast.showToast({ type: "warning", content: "msg.00002" }),
            )();
        },

        // Click Approval Request Button !== 승인 요청 버튼 클릭 ==!
        onAprvRqstBtnClick: () => {
            if (comnUtils.isEmpty(form.usrAplyInfo.getValue("authAprvRqstList"))) {
                modal.openModal({
                    content: "msg.ptl.00807", // Please designate the approver. !== 결재자를 지정해주세요. ==!
                    onCancel: () => {
                        modal.postMessage({
                            aprvId: "",
                        });
                    },
                    onClose: () => {
                        modal.postMessage({
                            aprvId: "",
                        });
                    },
                });
                return;
            }

            form.usrAplyInfo.handleSubmit(
                () => {
                    modal.openModal({
                        //url: "/comn/comn/ppup/aprvRqstDrt",
                        url: "/comn/comn/ppup/roleAprvRqstDrt",
                        params: {
                            bsopClsfCd: "PTL",
                            scrnDocCd: "scrn_ptla0001",
                            aprvReffId: "/" + form.usrAplyInfo.getValue("usrDmnId"),
                            aprvAfPrcssCfgnId: "cfgn_ptl0001", // 내부사용자 등록 및 권한 등록
                            rjctAfPrcssCfgnId: "cfgn_ptl0004",
                            preStageRtrnUseYn: "Y", // 이전단계 반려처리가 필요한 경우에만 'Y'로 넘겨 줄 것 (결재처리 시 이전단계반려 라디오버튼을 보여줌)
                            itsfAprvYn: "", // 업무팀 자체 결재처리 시 'Y'로 넘겨 줄 것 (결재처리탭 비활성화)
                            aplyUsrDmnId: form.usrAplyInfo.getValue("usrDmnId"),
                            delYn: "N",
                            aprvRqstTtleNm: "Apply for internal user registration",
                            aprvRqstCn: "contents",
                            //aprvUsrList: form.usrAplyInfo.getValue("aprvUsrList"),
                            authAprvRqstList: form.usrAplyInfo.getValue("authAprvRqstList"),
                            docNo: form.usrAplyInfo.getValue("usrDmnId"),
                            testCallback: (data: any) => {
                                if (!comnUtils.isEmpty(data.aprvId)) {
                                    setBtns((btns: any) => ({ ...btns, regist: false }));

                                    form.usrAplyInfo.setValue("aprvId", data.aprvId);

                                    const formData = form.usrAplyInfo.getValues();
                                    if (typeof formData.fileGrpUuid === "object") {
                                        formData.fileSaveDto = formData.fileGrpUuid;
                                        formData.fileGrpUuid = null;
                                    }
                                    fetch.saveUsrRgsrAply.fetch(formData);
                                    handler.saveUsrAplyId();
                                }
                            },
                        },
                        size: "md",
                        draggable: true,
                    });
                },
                () => toast.showToast({ type: "warning", content: "msg.00002" }),
            )();
        },
    };

    useEffect(() => {
        if (menuId === "elecAprv") {
            setBtns((btns: any) => ({ ...btns, regist: false, search: false }));

            const aprvId = searchParams.get("aprvId");
            fetch.getUsrInfo.fetch({ usrDmnId, aprvId });
            form.usrAplyInfo.setEditable(false);
        } else {
            form.usrAplyInfo.setEditable("aprvPrcssStatCdNm", false);
            if (auth.get("usrId") !== undefined) fetch.getUsrInfo.fetch(auth.get("usrId"));
        }
    }, []);

    const orgYn = form.usrAplyInfo.watch("actnUsrYn");

    useEffect(() => {
        if (!btns.regist) return;
        form.usrAplyInfo.setValue("fileGrpUuid", null);

        form.usrAplyInfo.setValue("cstmSctnOrgzCd", "");
        form.usrAplyInfo.setValue("cstmOfceCd", "");
        form.usrAplyInfo.setValue("cstmUnitOrgzCd", "");
        form.usrAplyInfo.setValue("roleId", "");
        form.usrAplyInfo.setValue("aprvPrcssPathMgmtId", "APRV00000003");
    }, [orgYn]);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Title title={"L_PSN_INFO"} titleSize={2} />
                            <Group.Row>
                                <Group.Control
                                    {...form.usrAplyInfo.schema.cstmOfcrNo}
                                    rightButton={{ icon: "search", onClick: handler.getUsrRgsrAplyInfo }}
                                    onChange={handler.changeCstmOfcrNo}
                                    required={btns.regist}
                                />
                                <Group.Control
                                    {...form.usrAplyInfo.schema.usrTin}
                                    rightButton={{ icon: "search", onClick: handler.getUsrTinInfo }}
                                    onChange={handler.changeUsrTin}
                                    required={btns.regist}
                                />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control
                                    {...form.usrAplyInfo.schema.usrDmnId}
                                    readOnly={true}
                                    required={btns.regist}
                                />
                                <Group.Control
                                    {...form.usrAplyInfo.schema.usrNm}
                                    readOnly={true}
                                    required={btns.regist}
                                />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control
                                    {...form.usrAplyInfo.schema.usrTelno}
                                    readOnly={true}
                                    required={btns.regist}
                                />
                                <Group.Control
                                    {...form.usrAplyInfo.schema.usrEmlAddr}
                                    readOnly={true}
                                    required={btns.regist}
                                />
                            </Group.Row>
                            <Group.Row hidden={!btns.regist}>
                                <Group.Control {...form.usrAplyInfo.schema.usrPwd} />
                                <Group.Control {...form.usrAplyInfo.schema.usrPwdCfrm} onBlur={handler.checkPwd} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control
                                    {...form.usrAplyInfo.schema.actnUsrYn}
                                    label={"L_APLY_TP"}
                                    onChange={handler.changeActnUsrYn}
                                />
                                {btns.search && (
                                    <Group.Control {...form.usrAplyInfo.schema.aprvPrcssStatCdNm} readOnly={true} />
                                )}
                            </Group.Row>
                            <Group.Row hidden={(btns.regist && orgYn === "N") || !btns.search}>
                                <Group.Control {...form.usrAplyInfo.schema.fileGrpUuid} copy={false} />
                            </Group.Row>
                        </Group.Section>

                        <Group.Section hidden={btns.regist}>
                            <Group.Title title={"T_AFFIL"} titleSize={2} />
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.cstmSctnOrgzCd} required={false} />
                                <Group.Control {...form.usrAplyInfo.schema.roleId} required={false} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.cstmOfceCd} required={false} />
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.cstmUnitOrgzCd} required={false} />
                            </Group.Row>
                        </Group.Section>

                        <Group.Section hidden={!btns.regist || orgYn === "N"}>
                            <Group.Title title={"T_AFFIL"} titleSize={2} />
                            <Group.Row>
                                <Group.Control
                                    {...form.usrAplyInfo.schema.cstmSctnOrgzCd}
                                    popupParams={{ cstmOrgzTpCd: "S" }}
                                    onChange={() => handler.getCstmList("N")}
                                    required={requiredYn == "Y"}
                                ></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control
                                    {...form.usrAplyInfo.schema.cstmOfceCd}
                                    onChange={() => handler.getUnitList("N")}
                                    popupParams={{ cstmOfceCdArr: form.usrAplyInfo.getValue("cstmOfceCdArr") }}
                                ></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control
                                    {...form.usrAplyInfo.schema.cstmUnitOrgzCd}
                                    onChange={() => handler.getCstmRoleList("N")}
                                ></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.roleId} required={requiredYn == "Y"} />
                            </Group.Row>
                        </Group.Section>

                        <Layout direction="row">
                            <Layout.Right>
                                {btns.search && (
                                    <>
                                        <Button
                                            hidden={orgYn === "Y"}
                                            role="save"
                                            onClick={() => handler.saveUsrRgsrAply("S")}
                                        />
                                        <Button
                                            hidden={
                                                orgYn === "N" || !comnUtils.isEmpty(form.usrAplyInfo.getValue("aprvId"))
                                            }
                                            variant="primary"
                                            onClick={() => handler.saveUsrRgsrAply("A")}
                                        >
                                            {t("B_APRV_RQST")}
                                        </Button>
                                    </>
                                )}
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>
        </Page>
    );
};
